import {
  MDXEditor,
  headingsPlugin,
  listsPlugin,
  quotePlugin,
  thematicBreakPlugin,
  markdownShortcutPlugin,
  linkPlugin,
  linkDialogPlugin,
  tablePlugin,
  toolbarPlugin,
  UndoRedo,
  Separator,
  BoldItalicUnderlineToggles,
  StrikeThroughSupSubToggles,
  ListsToggle,
  BlockTypeSelect,
  CreateLink,
  InsertThematicBreak,
  InsertTable,
  type MDXEditorMethods
} from "@mdxeditor/editor";
import "@mdxeditor/editor/style.css";
import { useTranslation } from "react-i18next";
import type { JSX, Ref } from "react";
import { cn } from "@/lib/utils";
import { useMemo } from "react";

interface CustomMDXEditorProps {
  /** The current markdown/string content */
  value?: string;
  /** Handler for content changes */
  onChange?: (_value: string) => void;
  /** Placeholder text for the editor */
  placeholder?: string;
  /** Whether the editor is read-only */
  readOnly?: boolean;
  /** Auto-focus the editor on mount */
  autoFocus?: boolean;
  /** Whether to show the toolbar */
  showToolbar?: boolean;
  /** The class name for the editable content */
  editableContentClassName?: string;
  /** Whether to overwrite the default classes */
  overwriteDefaultClasses?: boolean;
  /** Ref for the MDXEditor instance */
  ref?: Ref<MDXEditorMethods>;
};

/**
 * A customized reusable MDX editor component that provides rich text editing capabilities
 * with markdown support. It includes various plugins for formatting, lists,
 * links, tables and more.
 *
 * @component
 * @param {CustomMDXEditorProps} props - The component props
 * @param {string} [props.value=""] - The current markdown/string content
 * @param {Function} [props.onChange] - Handler for content changes
 * @param {string} [props.placeholder=""] - Placeholder text for the editor
 * @param {boolean} [props.readOnly=false] - Whether the editor is read-only
 * @param {boolean} [props.autoFocus=false] - Auto-focus the editor on mount
 * @param {boolean} [props.showToolbar=true] - Whether to show the toolbar
 * @param {string} [props.editableContentClassName=""] - The class name for the editable content
 * @param {boolean} [props.overwriteDefaultClasses=false] - Whether to overwrite the default classes
 * @param {Ref<MDXEditorMethods>} [props.ref] - Ref for the MDXEditor instance
 * @returns {JSX.Element} The rendered MDX editor component
 */
const CustomMDXEditor = (props: CustomMDXEditorProps): JSX.Element => {
  const {
    value = "",
    onChange = () => { },
    placeholder = "",
    readOnly = false,
    autoFocus = false,
    showToolbar = true,
    editableContentClassName = "",
    overwriteDefaultClasses = false,
    ref,
  } = props;

  const { t } = useTranslation(["components", "common"]);

  const basePlugins = useMemo(() => [
    headingsPlugin({ allowedHeadingLevels: [1, 2, 3, 4] }),
    listsPlugin(),
    quotePlugin(),
    thematicBreakPlugin(),
    markdownShortcutPlugin(),
    linkPlugin(),
    linkDialogPlugin(),
    tablePlugin(),
  ], []);

  const toolbarComponent = useMemo(() => {
    if (readOnly) return null;
    return (
      <>
        <UndoRedo />
        <Separator />
        <BoldItalicUnderlineToggles />
        <Separator />
        <StrikeThroughSupSubToggles />
        <Separator />
        <ListsToggle />
        <Separator />
        <BlockTypeSelect />
        <Separator />
        <CreateLink />
        <InsertThematicBreak />
        <InsertTable />
      </>
    );
  }, [readOnly]);

  const plugins = useMemo(() => {
    if (!showToolbar) return basePlugins;

    return [
      ...basePlugins,
      toolbarPlugin({
        toolbarContents: () => toolbarComponent,
      }),
    ];
  }, [basePlugins, showToolbar, toolbarComponent]);

  return (
    <MDXEditor
      ref={ref}
      markdown={value}
      onChange={onChange}
      placeholder={placeholder}
      translation={(key, defaultValue, interpolations) => {
        return t(key, { defaultValue, ...interpolations });
      }}
      readOnly={readOnly}
      autoFocus={autoFocus}
      contentEditableClassName={cn(
        "prose prose-p:my-2 leading-tight max-w-full",
        !overwriteDefaultClasses && "h-[55vh] overflow-y-auto p-4 border-[1px] border-gray-300 rounded-md",
        editableContentClassName
      )}
      plugins={plugins}
      className="relative [--mdxeditor-floating-toolbar-z-index:100] [--mdxeditor-popup-z-index:100]"
    />
  );
};

CustomMDXEditor.displayName = "CustomMDXEditor";

export default CustomMDXEditor;
